import { useState, useRef, useCallback, useEffect } from 'react';

interface MobileBottomSheetProps {
  children: React.ReactNode;
}

// Snap points as percentage of viewport height from the bottom
const SNAP_COLLAPSED = 0.25; // 25% — peek at a few cards
const SNAP_HALF = 0.5; // 50% — split view
const SNAP_EXPANDED = 0.85; // 85% — mostly cards

const DRAG_THRESHOLD = 30; // px minimum drag to trigger snap change

export const MobileBottomSheet = ({ children }: MobileBottomSheetProps) => {
  const [sheetHeight, setSheetHeight] = useState(SNAP_COLLAPSED);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragStartHeight = useRef<number>(SNAP_COLLAPSED);

  const snapToNearest = useCallback((height: number) => {
    const snaps = [SNAP_COLLAPSED, SNAP_HALF, SNAP_EXPANDED];
    let closest = snaps[0];
    let minDist = Math.abs(height - snaps[0]);
    for (const snap of snaps) {
      const dist = Math.abs(height - snap);
      if (dist < minDist) {
        minDist = dist;
        closest = snap;
      }
    }
    return closest;
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    dragStartHeight.current = sheetHeight;
  }, [sheetHeight]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return;

    const deltaY = dragStartY.current - e.touches[0].clientY;
    const deltaPercent = deltaY / window.innerHeight;
    const newHeight = Math.min(SNAP_EXPANDED, Math.max(SNAP_COLLAPSED, dragStartHeight.current + deltaPercent));

    setSheetHeight(newHeight);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (dragStartY.current === null) return;

    const dragDistance = Math.abs(sheetHeight - dragStartHeight.current) * window.innerHeight;

    if (dragDistance < DRAG_THRESHOLD) {
      // Not enough drag — snap back
      setSheetHeight(dragStartHeight.current);
    } else {
      // Snap to nearest point
      setSheetHeight(snapToNearest(sheetHeight));
    }

    dragStartY.current = null;
  }, [sheetHeight, snapToNearest]);

  // Prevent body scroll when sheet is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div
      ref={sheetRef}
      className="absolute bottom-0 left-0 right-0 bg-gray-50 rounded-t-2xl shadow-2xl z-10 flex flex-col"
      style={{
        height: `${sheetHeight * 100}vh`,
        transition: dragStartY.current !== null ? 'none' : 'height 0.3s ease-out',
      }}
    >
      {/* Drag handle */}
      <div
        className="flex-shrink-0 flex justify-center py-3 cursor-grab active:cursor-grabbing touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="w-10 h-1 rounded-full bg-gray-300" />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {children}
      </div>
    </div>
  );
};
